import { Link, useParams, useSearchParams } from "react-router-dom";
import { ProductCard } from "@/components/shop/ProductCard";
import { ShowMore, useShowMore } from "@/components/shop/ShopBits";
import { countByPattern, getPattern, getPatterns, getProductsWithPattern, textureSrc } from "@/lib/catalog";
import { familyNames, familyOf } from "@/lib/patternFamilies";
import { combosFor } from "@/lib/combos";
import { isOnOrder, isSoldOut, onOrderMessage } from "@/lib/patternAvailability";
import { usePageMeta } from "@/lib/seo";
import NotFoundPage from "./NotFoundPage";

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" data-testid={testId} aria-pressed={active} onClick={onClick} className={`min-h-[44px] shrink-0 rounded-full border px-5 text-sm transition-colors duration-300 ${active ? "border-bordeaux bg-bordeaux text-white" : "border-ink/15 text-ink hover:border-bordeaux/50"}`}>
    {children}
  </button>
);

const available = () => getPatterns().map((p) => ({ ...p, count: countByPattern(p.id), family: familyOf(p.id) })).filter((p) => p.count > 0);

export function PatternsPage() {
  const [params, setParams] = useSearchParams();
  const all = available();
  const families = familyNames(all.map((p) => p.id));
  const family = families.includes(params.get("familia")) ? params.get("familia") : null;
  const list = all.filter((p) => !family || p.family === family);
  const pg = useShowMore(list, 24, family ?? "");
  usePageMeta("Tecidos e padrões", "Todos os tecidos e padrões disponíveis nas peças da Babybib, para escolheres com calma.");
  const pick = (l) => setParams(l ? { familia: l } : {});

  return (
    <div className="bb-container pb-16 pt-12 sm:pt-16">
      <p className="bb-eyebrow mb-4">Tecidos e padrões</p>
      <h1 data-testid="patterns-title" className="font-serif text-[2.1rem] font-normal leading-none tracking-tight sm:text-6xl">Escolhe pelo tecido</h1>
      <p className="mt-5 max-w-xl text-base text-taupe">{all.length} padrões. Escolhe um para ver os modelos em que está disponível.</p>
      <div className="sticky top-[var(--hdr,60px)] z-20 transition-[top] duration-300 -mx-5 mt-8 bg-ivory/90 px-5 py-3 backdrop-blur-xl sm:-mx-8 sm:px-8">
        <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]" role="group" aria-label="Filtrar por família">
          <Chip active={!family} onClick={() => pick(null)} testId="patterns-family-todos">Todos</Chip>
          {families.map((l) => <Chip key={l} active={family === l} onClick={() => pick(l)} testId={`patterns-family-${l}`}>{l}</Chip>)}
        </div>
      </div>
      <div className="mt-6 grid grid-cols-3 gap-x-2.5 gap-y-6 sm:grid-cols-4 sm:gap-x-5 sm:gap-y-8 lg:grid-cols-5 xl:grid-cols-6" data-testid="patterns-grid">
        {pg.shown.map((p) => (
          <Link key={p.id} to={`/tecidos/${p.id}`} data-testid={`pattern-card-${p.id}`} className="group block">
            <div className="relative aspect-square overflow-hidden rounded-lg bg-linen">
              <img src={textureSrc(p.texture, 240)} alt="" loading="lazy" className={`absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04] ${isSoldOut(p) ? "opacity-45 saturate-0" : ""}`} />
              {isSoldOut(p) && <span className="absolute inset-x-2 bottom-2 rounded-full bg-ivory/95 px-2 py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-taupe-strong" data-testid={`pattern-card-sold-out-${p.id}`}>Esgotado</span>}
            </div>
            <p className="mt-2 text-[13px] font-medium leading-tight text-ink sm:mt-3 sm:text-sm">{p.name}</p>
            <p className="mt-0.5 text-[11px] text-taupe sm:mt-1 sm:text-xs">{p.count === 1 ? "1 modelo" : `${p.count} modelos`}{isOnOrder(p) && <span className="text-bordeaux"> · por encomenda</span>}</p>
          </Link>
        ))}
      </div>
      <ShowMore remaining={pg.remaining} step={pg.step} onClick={pg.more} testId="patterns-show-more" />
    </div>
  );
}

export function PatternDetailPage() {
  const { id } = useParams();
  const pattern = getPattern(id);
  usePageMeta(pattern ? pattern.name : "Tecido", pattern ? `Modelos Babybib disponíveis em ${pattern.name}.` : "");
  if (!pattern) return <NotFoundPage />;
  const products = getProductsWithPattern(id);
  const pairs = combosFor(id).map(getPattern).filter(Boolean);
  const same = available().filter((p) => p.family === familyOf(id) && p.id !== id).slice(0, 6);
  return (
    <div className="bb-container pb-16 pt-8 sm:pt-12">
      <Link to="/tecidos" data-testid="pattern-back-link" className="inline-flex min-h-[44px] items-center text-sm text-taupe hover:text-bordeaux">← Tecidos e padrões</Link>
      <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-5">
          <div role="img" aria-label={`Tecido ${pattern.name}`} className="aspect-square rounded-lg bg-cover bg-center" style={{ backgroundImage: `url(${textureSrc(pattern.texture, 800)})` }} data-testid="pattern-detail-image" />
        </div>
        <div className="lg:col-span-7 lg:self-center">
          <p className="bb-eyebrow mb-3">{familyOf(id)}</p>
          <h1 data-testid="pattern-detail-title" className="font-display text-4xl leading-tight sm:text-5xl">{pattern.name}</h1>
          <p className="mt-4 text-base text-taupe">{products.length === 1 ? "Disponível em 1 modelo." : `Disponível em ${products.length} modelos.`}{pattern.noName ? " Este tecido não permite nome personalizado." : ""}</p>
          {isSoldOut(pattern) && <p className="mt-4 rounded-2xl bg-linen px-4 py-3 text-sm text-taupe-strong" data-testid="pattern-detail-sold-out">Este padrão está esgotado de momento, por isso não se pode escolher para novas encomendas.</p>}
          {isOnOrder(pattern) && <p className="mt-4 rounded-2xl bg-rose-soft/80 px-4 py-3 text-sm text-ink" data-testid="pattern-detail-on-order">{onOrderMessage(pattern)}</p>}
          {pairs.length > 0 && (
            <div className="mt-8" data-testid="pattern-combos">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-taupe-strong">Combina bem com</p>
              <div className="flex flex-wrap gap-4">
                {pairs.map((p) => (
                  <Link key={p.id} to={`/tecidos/${p.id}`} className="flex items-center gap-3 rounded-2xl bg-linen/70 p-2 pr-4 text-sm text-ink transition-colors hover:bg-rose-soft">
                    <img src={textureSrc(p.texture, 240)} alt="" loading="lazy" className="h-12 w-12 rounded-xl object-cover" />{p.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {same.length > 0 && (
            <div className="mt-8">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-taupe-strong">Outros em {familyOf(id)}</p>
              <div className="flex flex-wrap gap-3">
                {same.map((p) => (
                  <Link key={p.id} to={`/tecidos/${p.id}`} title={p.name} className="block h-14 w-14 overflow-hidden rounded-lg shadow-[inset_0_0_0_1px_rgba(64,55,49,.12)] transition-transform hover:-translate-y-0.5">
                    <img src={textureSrc(p.texture, 240)} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <section className="mt-16" data-testid="pattern-products">
        <h2 className="font-display text-2xl sm:text-3xl">Modelos em {pattern.name}</h2>
        <div className="mt-8 grid grid-cols-2 gap-x-3 gap-y-10 sm:gap-x-6 lg:grid-cols-4">
          {products.map((p) => <ProductCard key={p.slug} product={p} patternId={id} />)}
        </div>
      </section>
    </div>
  );
}
